import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, ChevronRight, X } from 'lucide-react';
import { useState } from 'react';

interface CriticalAlert {
  id: string;
  type: 'stock' | 'dlc';
  message: string;
  count: number;
}

interface CriticalAlertsBannerProps {
  criticalStocks: number;
  expiringLots: number;
}

export function CriticalAlertsBanner({ criticalStocks, expiringLots }: CriticalAlertsBannerProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const alerts: CriticalAlert[] = [];

  if (criticalStocks > 0) {
    alerts.push({
      id: 'stock',
      type: 'stock',
      message: `${criticalStocks} produit${criticalStocks > 1 ? 's' : ''} en rupture critique`,
      count: criticalStocks,
    });
  }

  if (expiringLots > 0) {
    alerts.push({
      id: 'dlc',
      type: 'dlc',
      message: `${expiringLots} lot${expiringLots > 1 ? 's' : ''} arrive${expiringLots > 1 ? 'nt' : ''} à expiration`,
      count: expiringLots,
    });
  }

  if (alerts.length === 0) return null;

  const totalAlerts = criticalStocks + expiringLots;

  return (
    <div className="mb-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 text-white shadow-lg relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white rounded-full"></div>
        <div className="absolute -left-5 -bottom-5 w-24 h-24 bg-white rounded-full"></div>
      </div>

      <div className="relative flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6" />
          </div>

          <div>
            <h3 className="font-bold text-lg">
              {totalAlerts} alerte{totalAlerts > 1 ? 's' : ''} critique{totalAlerts > 1 ? 's' : ''}
            </h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/90 mt-1">
              {alerts.map((alert) => (
                <button
                  key={alert.id}
                  onClick={() => navigate(alert.type === 'stock' ? '/inventory' : '/lots')}
                  className="flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  {alert.type === 'dlc' ? (
                    <Calendar className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  <span>{alert.message}</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={() => setDismissed(true)}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
